import React, { useCallback, useMemo } from 'react'
import { When } from 'react-if'
import { LinkButton } from '@/atoms/Button'
import { DocumentScroller } from '@/organisms/DocumentScroller'
import { Downloadable } from '@/services/DownloadablesService'
import { useUser } from '@/services/UserService'
import { useSafeTrack } from '@/utils/analytics'
import { isDefined } from '@/utils/primitives/generics'
import { useTranslate } from '@/utils/translate/translate-client'

interface DownloadsTabProps {
  downloadables?: Downloadable[]
  projectSlug: string
}

const MAX_DOWNLOADS_DISPLAY = 10

export const DownloadsTab: React.FC<DownloadsTabProps> = ({ downloadables, projectSlug }) => {
  const track = useSafeTrack()
  const { isLoggedIn } = useUser()
  const { t } = useTranslate('watch')

  const handleViewAllClick = useCallback(() => {
    track('Watch Project Downloads View All Clicked', { project: projectSlug })
  }, [track, projectSlug])

  const filteredDownloads = useMemo(() => {
    return downloadables?.filter((downloadable) => downloadable?.image && downloadable?.href).filter(isDefined) || []
  }, [downloadables])

  const displayedDownloads = useMemo(() => {
    return filteredDownloads.slice(0, MAX_DOWNLOADS_DISPLAY)
  }, [filteredDownloads])

  const hasMoreDownloads = filteredDownloads.length > MAX_DOWNLOADS_DISPLAY

  return (
    <>
      <When condition={displayedDownloads.length > 0}>
        <DocumentScroller
          authenticated={Boolean(isLoggedIn)}
          downloadables={displayedDownloads}
          project={projectSlug}
          returnTo={`/watch/${projectSlug}#downloadables`}
          className="grid h-full w-full grid-cols-2 !p-0 md:flex md:flex-row md:flex-wrap"
          cardClassName="w-fit md:w-[268px]"
          imageClassName="md:!h-[338px] md:!w-[252px] !w-[168px] !h-[224px] se:!w-[150px] se:!h-[200px] mx-auto"
        />
        {hasMoreDownloads && (
          <div className="flex justify-start pt-4">
            <LinkButton
              href={`/watch/${projectSlug}/downloads`}
              className="mb-16 w-full md:w-fit"
              variant="white"
              outline
              onClick={handleViewAllClick}
            >
              {t('viewAllDownloads', 'View All Downloads')}
            </LinkButton>
          </div>
        )}
      </When>
    </>
  )
}
